import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Alert, AlertTitle, Box, Button, CssBaseline } from '@mui/material';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { isMobileApp } from 'webui/utils/version';
import { preventDefault, useCallbacks } from 'shared/hooks/utils';
import { logout } from 'console/redux/main-actions';
import 'app/style/_small.sass';
import { isMediumUI, isSmallUI } from 'shared/app/lib/choose-ui-type';
import { select_ui_settings, set_ui_settings } from 'console/redux/settings-actions';
import { WindowContextProvider } from 'shared/contexts/window';
import { is_paid_tariff } from 'console/billing/billing-utils';
import { LazyMotion } from 'framer-motion';
import { LeftMenuContainer } from '../left-menu';
import { TabContextProvider } from 'console/navigation/context';
import { Navigation } from '../navigation';
import { SmallHeader } from '../header';
import { CornerNotifications } from 'widgets/corner-notification/ui';
import { MainContent } from './content';
import { UiGuideViewer } from 'console/ui-guide';
import { zontTabsDeviceSelector } from 'console/tabs/tabs-list';
import { selectNullableSelectedDeviceId } from 'entities/device/lib';
import { Show } from 'shared/components/utils';
import { activeMapDashboard } from 'pages/dashboard.map/model/store';
import { StaffBanner } from 'entities/device.acl/ui/banner';
const loadFeatures = () => import('shared/features/lazy/features').then((res) => res.default);
const getMenuOpened = (state) => state.ui_settings.leftpanel != null &&
    state.ui_settings.leftpanel.collapsed !== true;
const style = {
    transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
};
export const ZontContent = memo(function ZontNextContent(props) {
    const { ui } = props;
    const is_small_ui = isSmallUI();
    const paid_tariff_on = useAppSelector(is_paid_tariff);
    const { leftpanel, launch_parameter } = useAppSelector(select_ui_settings);
    const setUiSettings = useAction(set_ui_settings);
    const menu_opened = useAppSelector(getMenuOpened);
    const closeMenu = useCallback(() => setUiSettings({ leftpanel: { collapsed: true } }), []);
    const selected_device_id = useAppSelector(selectNullableSelectedDeviceId);
    const device = useAppSelector(zontTabsDeviceSelector);
    const legacy_device = ui.devices.device(selected_device_id);
    const openMenu = useCallback(() => setUiSettings({ leftpanel: { collapsed: false } }), []);
    const onMapDashboardClick = useCallbacks([
        useAction(activeMapDashboard),
        closeMenu,
    ]);
    useEffect(() => {
        if (launch_parameter === 'map') {
            paid_tariff_on
                ? onMapDashboardClick()
                : setUiSettings({ launch_parameter: 'currentItem' });
        }
    }, []);
    const onOpenMenuClick = useCallback(() => {
        if (!is_small_ui) {
            setUiSettings({ leftpanel: { collapsed: menu_opened } });
            return;
        }
        openMenu();
    }, [leftpanel]);
    const is_demo = useAppSelector((state) => state.account.is_demo);
    const onExitDemo = useCallbacks([preventDefault, useAction(logout)]);
    const has_device = useMemo(() => legacy_device || device, [device, legacy_device]);
    return (_jsx(WindowContextProvider, { children: _jsxs(ErrorBoundary, { children: [_jsx(ErrorBoundary, { children: _jsx(UiGuideViewer, {}) }), _jsxs(LazyMotion, { features: loadFeatures, children: [_jsxs(Box, { className: "MobileUi", display: "flex", flexDirection: "row", children: [_jsx(CssBaseline, {}), _jsx(Show, { when: has_device, children: _jsx(LeftMenuContainer, { opened: menu_opened, open: openMenu, close: closeMenu }) }), _jsxs(Box, { display: "flex", flexDirection: "column", width: has_device && menu_opened && isMediumUI()
                                        ? `calc(100% - 280px)`
                                        : '100%', sx: style, height: "100%", children: [has_device && (_jsx(ErrorBoundary, { children: _jsx(SmallHeader, { menu_opened: menu_opened, onClick: onOpenMenuClick }) })), _jsx(StaffBanner, {}), _jsx(Show, { when: isMobileApp() && is_demo, children: _jsxs(Alert, { severity: "info", variant: "filled", action: _jsx(Button, { onClick: onExitDemo, variant: "contained", children: "\u0412\u042B\u0419\u0422\u0418" }), children: [_jsx(AlertTitle, { children: "\u0414\u0415\u041C\u041E-\u0420\u0415\u0416\u0418\u041C" }), "\u041C\u043E\u0436\u043D\u043E \u043F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C \u0438\u043D\u0442\u0435\u0440\u0444\u0435\u0439\u0441 \u0440\u0430\u0437\u043D\u044B\u0445 \u043F\u0440\u0438\u0431\u043E\u0440\u043E\u0432"] }) }), _jsx(TabContextProvider, { ui: ui, device: device, legacy_device: legacy_device, children: _jsxs(Box, { "data-tooltip": "main-content-and-navigation", display: "flex", flexDirection: is_small_ui ? 'column' : 'column-reverse', overflow: "auto", justifyContent: "flex-end", height: "100%", children: [_jsx(ErrorBoundary, { children: _jsx(MainContent, { ...props }) }), _jsx(ErrorBoundary, { children: _jsx(Navigation, {}) })] }) })] })] }), _jsx(CornerNotifications, {})] })] }) }));
});
